import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { TableOptions } from "../../../../shared-kernel/components/Table";
import {
  createAdapterToFieldInvestmentTypeEndpoint,
  createAdapterToInvestmentType,
  createAdapterToInvestmentTypeEndpoint
} from "../adapters/investmentType.adapter";
import { EndpointInvestmentTypeFiltered, InvestmentType, InvestmentTypeEndpoint } from "../domain/InvestmentType.model";
import { appConfig } from "../../../../config";
import { EndpointInvestmentType } from "../domain/InvestmentType.model";
import { createAdapterToFilterBackend } from "../../../../utilities/paramsAdapter";

const BASE_INVESTMENT_TYPE_URL = '/entidadesfinancieras/tiposinversiones'
const BASE_INVESTMENT_TYPE_MUTATION_URL = '/entidadesfinancieras/tipoinversion'
export const BASE_EXPORT_URL = `${appConfig.API_NODE_URL}/entidadesfinancieras/tiposinversiones/export`;

export const investmentTypeApi = createApi({
  reducerPath: "investmentTypeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: appConfig.API_NODE_URL,
    prepareHeaders: (headers: Headers) => {
      const APP_TOKEN = "app_token";
      const token = localStorage.getItem(APP_TOKEN);

      if (token) {
        headers.set("authorization", `${token}`);
      }

      return headers;
    },
    credentials: "include",
  }),
  refetchOnMountOrArgChange: true,
  tagTypes: ["InvestmentTypes", "InvestmentType"],
  endpoints: (builder) => ({
    getInvestmentType: builder.query<InvestmentType, number | undefined>({
      query: (id) => `${BASE_INVESTMENT_TYPE_MUTATION_URL}/${id}`,
      transformResponse: (response: EndpointInvestmentType) => {
        return createAdapterToInvestmentType(response.Content)
      },
      providesTags: (_res, _err, id) => [{type: "InvestmentType", id: id?.toString()}],
    }),
    getInvestmentTypes: builder.query<any, TableOptions<InvestmentType>|undefined>({
      query: (params: TableOptions<InvestmentType>) => {
        const { page, pageSize, order, search, filters } = params || {};
        const adaptersFilter = filters?.map( f => createAdapterToFilterBackend(f,createAdapterToFieldInvestmentTypeEndpoint))
        const serializedFilters = adaptersFilter ? JSON.stringify(adaptersFilter) : undefined;
        const serializedOrder = order ? JSON.stringify({field:createAdapterToFieldInvestmentTypeEndpoint(order.field), order:order.order}) : undefined;
        return {
          url:`${BASE_INVESTMENT_TYPE_URL}/filtros`,
          params: {
            page:page? page - 1: 0,
            pageSize,
            order: serializedOrder,
            search,
            filters:serializedFilters
          },
        }
      },
      providesTags: ["InvestmentTypes"],
      transformResponse: (response: EndpointInvestmentTypeFiltered, _meta, params: TableOptions<InvestmentType>) => {
        const page = params?.page ?? 1;
        const endpointResponse = response?.rows?.map((investmentType: InvestmentTypeEndpoint) =>
            createAdapterToInvestmentType(investmentType)
        );
        return {
          data: endpointResponse,
          page,
          total: response.total,
        };
      },
    }),
    createInvestmentType: builder.mutation<any, InvestmentType>({
      query: (data) => ({
        url: `${BASE_INVESTMENT_TYPE_MUTATION_URL}`,
        method: "POST",
        body: {
          "Content": createAdapterToInvestmentTypeEndpoint(data)
        },
      }),
      invalidatesTags: ["InvestmentTypes"],
    }),
    updateInvestmentType: builder.mutation<any, InvestmentType>({
      query: (data: InvestmentType) => ({
        url: `${BASE_INVESTMENT_TYPE_MUTATION_URL}`,
        method: "PUT",
        body: {
          "Content": createAdapterToInvestmentTypeEndpoint(data)
        },
      }),
      invalidatesTags: (_res, _err, data: InvestmentType) => ["InvestmentTypes", {type: 'InvestmentType', id: data?.id?.toString()}],
    }),
    deleteInvestmentType: builder.mutation<void, number>({
      query: (id) => ({
        url: `${BASE_INVESTMENT_TYPE_MUTATION_URL}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["InvestmentTypes"],
    }),
  }),
});

export const {
  useGetInvestmentTypeQuery,
  useGetInvestmentTypesQuery,
  useCreateInvestmentTypeMutation,
  useUpdateInvestmentTypeMutation,
  useDeleteInvestmentTypeMutation
} = investmentTypeApi;
